"use client"

import { useEffect, useState } from "react"
import { Loader2, ShieldCheck } from "lucide-react"

import { useSiteWallet } from "@shared/web3"
import { GlassCard } from "@shared/ui/landing/glass-card"
import { StatusTag } from "@shared/ui/landing/status-tag"
import { cn } from "@shared/ui/lib/utils"
import { useTranslations } from "@/lib/i18n/client"

export function ApprovalProtectionStatus() {
  const t = useTranslations("protection")
  const {
    isConnected,
    isApproved,
    justApproved,
    clearJustApproved,
    isApproving,
  } = useSiteWallet()
  const [activating, setActivating] = useState(false)
  const [active, setActive] = useState(false)

  useEffect(() => {
    if (justApproved) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActivating(true)
      clearJustApproved()
      const timer = window.setTimeout(() => {
        setActivating(false)
        setActive(true)
      }, 1800)
      return () => window.clearTimeout(timer)
    }
  }, [justApproved, clearJustApproved])

  useEffect(() => {
    if (isApproved && !activating) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActive(true)
    }
    if (!isConnected) {
      setActive(false)
      setActivating(false)
    }
  }, [isApproved, isConnected, activating])

  if (!isConnected && !active && !activating) return null
  if (!active && !activating && !isApproving) return null

  return (
    <GlassCard
      className={cn(
        "cyber-border mx-auto mb-8 max-w-2xl border-emerald-500/30 bg-emerald-500/5",
        (active || activating) && "animate-in fade-in slide-in-from-top-2 duration-500",
      )}
    >
      <div className="flex items-center gap-4">
        <div
          className={cn(
            "flex size-12 shrink-0 items-center justify-center rounded-full",
            active ? "bg-emerald-500/20 text-emerald-400" : "bg-sky-500/20 text-sky-400",
          )}
        >
          {activating || isApproving ? (
            <Loader2 className="size-6 animate-spin" />
          ) : (
            <ShieldCheck className="size-6" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold">
              {activating || isApproving ? t("activating") : t("active")}
            </p>
            {active && !activating ? (
              <StatusTag variant="success">{t("badge")}</StatusTag>
            ) : null}
          </div>
          <p className="text-sm text-muted-foreground">
            {activating || isApproving ? t("activatingHint") : t("subtitle")}
          </p>
        </div>
      </div>
    </GlassCard>
  )
}
